import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Layout, Typography } from 'antd';
import { navigate } from '@reach/router';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { EventMessage, EventType, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { loginRequest } from 'authConfig';
import './login.css';

const { Content } = Layout;
const { Title, Text } = Typography;

// Simple 4-color square logo evoking the Microsoft/Entra ID brand, without any external asset.
function MicrosoftLogo() {
    return (
        <span className="login-ms-logo" aria-hidden="true">
            <span className="login-ms-logo__square login-ms-logo__square--orange" />
            <span className="login-ms-logo__square login-ms-logo__square--green" />
            <span className="login-ms-logo__square login-ms-logo__square--blue" />
            <span className="login-ms-logo__square login-ms-logo__square--yellow" />
        </span>
    );
}

interface LoginProps {
    path?: string;
}

// Login page - redirects the user to Microsoft Entra ID to sign in.
// On success the user is sent to /Home, on failure an error message is displayed.
function Login({ path }: LoginProps) {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [loginError, setLoginError] = useState<string | null>(null);

    // Already authenticated (e.g. cached session) -> go straight to the home page.
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/Home');
        }
    }, [isAuthenticated]);

    // Listen for MSAL login events to react to success/failure of the redirect flow.
    useEffect(() => {
        const callbackId = instance.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                setLoginError(null);
                navigate('/Home');
            }
            if (
                event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
                event.interactionType === InteractionType.Redirect
            ) {
                setLoginError("La connexion a échoué. Veuillez réessayer.");
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    const handleEntraIdLogin = () => {
        setLoginError(null);
        instance.loginRedirect(loginRequest).catch(() => {
            setLoginError("La connexion a échoué. Veuillez réessayer.");
        });
    };

    return (
        <Layout className="login-layout">
            <Content className="login-content">
                <Card className="login-card">
                    <Title level={3} className="login-title">Gestion des structures Colas</Title>
                    <Text type="secondary">Veuillez vous connecter pour continuer</Text>
                    {loginError && (
                        <Alert
                            type="error"
                            message={loginError}
                            showIcon
                            className="login-error"
                        />
                    )}
                    <div className="login-actions">
                        <Button
                            type="primary"
                            size="large"
                            block
                            icon={<MicrosoftLogo />}
                            loading={inProgress !== InteractionStatus.None}
                            onClick={handleEntraIdLogin}
                        >
                            Se connecter avec Microsoft Entra ID
                        </Button>
                    </div>
                </Card>
            </Content>
        </Layout>
    );
}

export default Login;
